import React from 'react';
import { useTranslation } from "react-i18next";
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Mail, Phone, Send, MessageCircle, Headphones, Building } from 'lucide-react';

const Contact = () => {
  const { t } = useTranslation();

  const contactInfo = [
    {
      icon: Mail,
      title: t("contact.info.email.title"),
      details: "stepperise.info@gmail.com",
      description: t("contact.info.email.desc"),
      color: 'text-yellow-600'
    },
    {
      icon: Phone,
      title: t("contact.info.phone.title"),
      details: "+7 (775) 692-6833",
      description: t("contact.info.phone.desc"),
      color: 'text-yellow-600'
    }
  ];

  const faqItems = [
    { question: t("contact.faq.q1"), answer: t("contact.faq.a1") },
    { question: t("contact.faq.q2"), answer: t("contact.faq.a2") },
    { question: t("contact.faq.q3"), answer: t("contact.faq.a3") },
    { question: t("contact.faq.q4"), answer: t("contact.faq.a4") }
  ];

  return (
    <div className="min-h-screen pt-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-6 bg-yellow-600 text-black px-4 py-2 text-sm font-medium">
            {t("contact.badge")}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-black">
            {t("contact.title")}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t("contact.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-black mb-6">
                {t("contact.info.title")}
              </h2>
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className={`p-3 rounded-lg bg-white shadow-sm ${info.color}`}>
                      <info.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-black">{info.title}</h3>
                      <p className="text-gray-700 font-medium">{info.details}</p>
                      <p className="text-gray-500 text-sm">{info.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <MessageCircle className="mr-2 h-5 w-5 text-yellow-600" />
                  {t("contact.quickLinks.title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <a href="#" className="block p-3 rounded-lg bg-yellow-50 hover:bg-yellow-100 transition-colors">
                  <div className="font-medium text-black">{t("contact.quickLinks.program.title")}</div>
                  <div className="text-sm text-gray-600">{t("contact.quickLinks.program.desc")}</div>
                </a>

                <a href="#" className="block p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="font-medium text-black">{t("contact.quickLinks.faq.title")}</div>
                  <div className="text-sm text-gray-600">{t("contact.quickLinks.faq.desc")}</div>
                </a>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border border-gray-200 shadow-xl">
              <CardHeader className="bg-black text-white rounded-t-lg">
                <CardTitle className="text-2xl font-bold flex items-center">
                  <Send className="mr-3 h-6 w-6" />
                  {t("contact.form.title")}
                </CardTitle>
                <CardDescription className="text-gray-300">
                  {t("contact.form.desc")}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-8 bg-white">
                {/* Formspree form */}
                <form 
                  action="https://formspree.io/f/xqaypgvd" 
                  method="POST"
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">{t("contact.form.name")}</Label>
                      <Input id="name" name="name" placeholder={t("contact.form.placeholderName")} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">{t("contact.form.email")}</Label>
                      <Input id="email" type="email" name="email" placeholder={t("contact.form.placeholderEmail")} required />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="category">{t("contact.form.category")}</Label>
                      <select 
                        id="category" 
                        name="category" 
                        className="w-full border-gray-300 rounded-md"
                        required
                      >
                        <option value="general">{t("contact.form.categories.general")}</option>
                        <option value="application">{t("contact.form.categories.application")}</option>
                        <option value="programs">{t("contact.form.categories.programs")}</option>
                        <option value="partnership">{t("contact.form.categories.partnership")}</option>
                        <option value="media">{t("contact.form.categories.media")}</option>
                        <option value="support">{t("contact.form.categories.support")}</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">{t("contact.form.subject")}</Label>
                      <Input id="subject" name="subject" placeholder={t("contact.form.placeholderSubject")} required />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{t("contact.form.message")}</Label>
                    <Textarea id="message" name="message" placeholder={t("contact.form.placeholderMessage")} rows={6} required />
                  </div>

                  <Button type="submit" className="w-full bg-black hover:bg-gray-800 text-yellow-400 font-semibold py-3 rounded-full">
                    {t("contact.form.submit")} <Send className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-black mb-4">{t("contact.faq.title")}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t("contact.faq.subtitle")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {faqItems.map((faq, index) => (
              <Card key={index} className="border border-gray-200 hover:shadow-xl">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-black flex items-start">
                    <Headphones className="mr-3 h-5 w-5 text-yellow-600 mt-1 flex-shrink-0" />
                    {faq.question}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <Card className="border-0 shadow-xl bg-black text-white">
            <CardContent className="p-12">
              <Building className="h-12 w-12 mx-auto mb-6 text-yellow-400" />
              <h3 className="text-2xl font-bold mb-4">{t("contact.cta.title")}</h3>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto">{t("contact.cta.desc")}</p>
              <Button asChild size="lg" className="bg-yellow-600 hover:bg-yellow-500 text-black font-semibold px-8 py-4 rounded-full">
                <a href="/register">{t("contact.cta.button")}</a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Contact;
